import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';

const API = (path, opts = {}) =>
  fetch(path, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
    ...opts,
  });

const STAGES = ['all','identified','contacted','demo_booked','proposal_sent','trial','converted','lost'];

const STAGE_COLOURS = {
  identified:    ['#dbeafe','#1e40af'],
  contacted:     ['#fef3c7','#92400e'],
  demo_booked:   ['#ede9fe','#5b21b6'],
  proposal_sent: ['#fee2e2','#991b1b'],
  trial:         ['#d1fae5','#065f46'],
  converted:     ['#dcfce7','#166534'],
  lost:          ['#f3f4f6','#6b7280'],
};

function stageBadge(stage) {
  const [bg, color] = STAGE_COLOURS[stage] || ['#f3f4f6','#6b7280'];
  return (
    <span style={{ background: bg, color, padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
      {(stage || '').replace(/_/g, ' ')}
    </span>
  );
}

function isOverdue(p) {
  if (!p.follow_up_date) return false;
  if (['converted','lost'].includes(p.stage)) return false;
  return new Date(p.follow_up_date) < new Date(new Date().toDateString());
}

// ── FCA Import Modal ──────────────────────────────────────────
function FcaImportModal({ onClose, onImported }) {
  const [mode, setMode] = useState('search'); // 'search' | 'network'
  const [query, setQuery] = useState('');
  const [principalFrn, setPrincipalFrn] = useState('');
  const [results, setResults] = useState([]);
  const [existingFrns, setExistingFrns] = useState(new Set()); // FRNs already in DB
  const [hiddenCount, setHiddenCount] = useState(0);           // how many were filtered out
  const [selected, setSelected] = useState({});
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [importResult, setImportResult] = useState(null);

  const KNOWN_NETWORKS = [
    { name: 'Openwork', frn: '408285' },
    { name: 'Legal & General Mortgage Club', frn: '183050' },
    { name: 'Primis Mortgage Network', frn: '234963' },
    { name: 'Quilter Financial Planning', frn: '440703' },
    { name: 'St. James\'s Place', frn: '171825' },
  ];

  const doSearch = async () => {
    setSearchError('');
    setResults([]);
    setSelected({});
    setExistingFrns(new Set());
    setHiddenCount(0);
    setImportResult(null);
    setSearching(true);
    try {
      const params = mode === 'network'
        ? `principal_frn=${encodeURIComponent(principalFrn)}`
        : `q=${encodeURIComponent(query)}`;
      const res = await API(`/api/prospects/fca-search?${params}`);
      const d = await res.json();
      if (!res.ok) { setSearchError(d.error || 'Search failed'); return; }
      const allFirms = d.results || [];
      if (allFirms.length === 0) { setSearchError('No results found'); return; }

      // Check which FRNs are already in the DB and filter them out of the list
      let existingSet = new Set();
      try {
        const chkRes = await API('/api/prospects/check-existing', {
          method: 'POST',
          body: JSON.stringify({ firms: allFirms }),
        });
        if (chkRes.ok) {
          const chkData = await chkRes.json();
          existingSet = new Set(chkData.existingFrns || []);
        }
      } catch (e) { /* best-effort — if check fails, show all */ }

      const newFirms = allFirms.filter(f => !existingSet.has(String(f.frn)));
      setExistingFrns(existingSet);
      setHiddenCount(allFirms.length - newFirms.length);
      setResults(newFirms);
      if (newFirms.length === 0) setSearchError('All firms from this search are already in your prospect list.');
    } catch (e) {
      setSearchError('Network error');
    } finally {
      setSearching(false);
    }
  };

  const toggleAll = () => {
    if (Object.keys(selected).length === results.length) {
      setSelected({});
    } else {
      const all = {};
      results.forEach(r => { all[r.frn] = r; });
      setSelected(all);
    }
  };

  const doImport = async () => {
    const firms = Object.values(selected);
    if (!firms.length) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await API('/api/prospects/fca-import', {
        method: 'POST',
        body: JSON.stringify({ firms }),
      });
      const d = await res.json();
      if (!res.ok) { setSearchError(d.error || 'Import failed'); return; }
      setImportResult(d);
      setSelected({});
      // Refresh the list for small synchronous imports; large background imports
      // won't have data yet so we skip the refresh (email will notify when done)
      if (d.status !== 'processing' && d.imported > 0) onImported();
    } catch (e) {
      setSearchError('Network error during import');
    } finally {
      setImporting(false);
    }
  };

  const selectedCount = Object.keys(selected).length;

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...modalStyle, maxWidth: '700px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>Import from FCA Register</h2>
            <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Search the FCA register and import firms as prospects</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}>&times;</button>
        </div>

        {/* Mode tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          {[['search','🔍 Search by name'],['network','🏢 By network / principal']].map(([m, label]) => (
            <button
              key={m}
              onClick={() => { setMode(m); setResults([]); setSearchError(''); setImportResult(null); setSelected({}); }}
              style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                background: mode === m ? '#0e1b2c' : '#f3f4f6',
                color: mode === m ? '#fff' : '#374151',
              }}
            >{label}</button>
          ))}
        </div>

        {/* Search controls */}
        {mode === 'search' ? (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <input
              placeholder="e.g. mortgage solutions, financial services…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && query.trim().length >= 2 && doSearch()}
              style={{ ...inputStyle, flex: 1 }}
            />
            <button
              onClick={doSearch}
              disabled={searching || query.trim().length < 2}
              style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 20px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >{searching ? 'Searching…' : 'Search'}</button>
          </div>
        ) : (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ ...labelStyle, marginBottom: '8px' }}>Select a known network or enter a principal FRN:</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
              {KNOWN_NETWORKS.map(n => (
                <button
                  key={n.frn}
                  onClick={() => setPrincipalFrn(n.frn)}
                  style={{
                    padding: '5px 12px', borderRadius: '999px', border: '1px solid', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
                    background: principalFrn === n.frn ? '#0e1b2c' : '#fff',
                    color: principalFrn === n.frn ? '#fff' : '#374151',
                    borderColor: principalFrn === n.frn ? '#0e1b2c' : '#d1d5db',
                  }}
                >{n.name}</button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                placeholder="Or enter principal FRN manually…"
                value={principalFrn}
                onChange={e => setPrincipalFrn(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && principalFrn.trim() && doSearch()}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={doSearch}
                disabled={searching || !principalFrn.trim()}
                style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 20px', fontWeight: 600, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >{searching ? 'Fetching all ARs… (10–15s)' : 'Load ARs'}</button>
            </div>
          </div>
        )}

        {searchError && <div style={alertRed}>{searchError}</div>}

        {/* Import result */}
        {importResult && importResult.status === 'processing' && (
          <div style={{ background: '#eff6ff', color: '#1e40af', padding: '12px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px', lineHeight: 1.5 }}>
            <strong>📥 Import started</strong> — {importResult.total} firms are being processed in {importResult.batches} batches.<br />
            You'll receive an email at <strong>{importResult.email}</strong> when the import is complete.
          </div>
        )}
        {importResult && importResult.status !== 'processing' && (
          <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
            ✓ Imported {importResult.imported} firm{importResult.imported !== 1 ? 's' : ''}
            {importResult.skipped > 0 && ` · ${importResult.skipped} already existed (skipped)`}
            {importResult.errors && importResult.errors.length > 0 && ` · ${importResult.errors.length} failed`}
          </div>
        )}

        {/* Results table */}
        {results.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {results.length} result{results.length !== 1 ? 's' : ''}
                {hiddenCount > 0 && (
                  <span style={{ marginLeft: '8px', color: '#9ca3af', fontStyle: 'italic' }}>
                    · {hiddenCount} already imported (hidden)
                  </span>
                )}
              </span>
              <button onClick={toggleAll} style={{ background: 'none', border: 'none', fontSize: '12px', color: '#10b981', cursor: 'pointer', fontWeight: 600 }}>
                {selectedCount === results.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', maxHeight: '320px', overflowY: 'auto', marginBottom: '16px' }}>
              {results.map((r, i) => (
                <label
                  key={r.frn || i}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                    borderBottom: i < results.length - 1 ? '1px solid #f3f4f6' : 'none',
                    cursor: 'pointer',
                    background: selected[r.frn] ? '#f0fdf4' : '#fff',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!selected[r.frn]}
                    onChange={e => setSelected(prev => {
                      const next = { ...prev };
                      if (e.target.checked) next[r.frn] = r; else delete next[r.frn];
                      return next;
                    })}
                    style={{ width: '15px', height: '15px', accentColor: '#10b981', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>FRN: {r.frn} · {r.type}</div>
                  </div>
                  <span style={{
                    fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '999px',
                    background: r.status === 'Authorised' || r.status === 'Authorised' ? '#d1fae5' : '#f3f4f6',
                    color: r.status === 'Authorised' ? '#065f46' : '#6b7280',
                    flexShrink: 0,
                  }}>{r.status || 'Active'}</span>
                </label>
              ))}
            </div>
            {selectedCount > 20 && (
              <p style={{ fontSize: '13px', color: '#1e40af', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px', padding: '8px 12px', marginBottom: '8px', textAlign: 'center' }}>
                📥 {selectedCount} firms selected — import will run in batches and you'll be emailed when complete
              </p>
            )}
            <button
              onClick={doImport}
              disabled={importing || selectedCount === 0}
              style={{
                background: selectedCount > 0 ? '#10b981' : '#d1d5db',
                color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px',
                fontWeight: 600, fontSize: '14px', cursor: selectedCount > 0 ? 'pointer' : 'default',
                width: '100%',
              }}
            >
              {importing ? 'Starting import…' : selectedCount > 0 ? `Import ${selectedCount} firm${selectedCount !== 1 ? 's' : ''} as prospects` : 'Select firms to import'}
            </button>
            <p style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center', marginTop: '8px' }}>
              Imports firm name, FRN and address. You can add email and contact details after importing.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default function OwnerProspects() {
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [stageFilter, setStageFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showFca, setShowFca] = useState(false);
  const [form, setForm] = useState({ company_name:'', contact_name:'', contact_email:'', contact_phone:'', firm_size:'', source:'', tier_interest:'', follow_up_date:'', notes:'' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (stageFilter !== 'all') params.set('stage', stageFilter);
    const res = await API(`/api/prospects?${params}`);
    const d = await res.json();
    if (!res.ok) { setError(d.error || 'Failed to load'); setLoading(false); return; }
    setProspects(Array.isArray(d) ? d : []);
    setLoading(false);
  }, [stageFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    const body = { ...form };
    if (body.firm_size) body.firm_size = parseInt(body.firm_size);
    const res = await API('/api/prospects', { method: 'POST', body: JSON.stringify(body) });
    const d = await res.json();
    setSaving(false);
    if (!res.ok) { setFormError(d.error || 'Failed'); return; }
    setShowAdd(false);
    setForm({ company_name:'', contact_name:'', contact_email:'', contact_phone:'', firm_size:'', source:'', tier_interest:'', follow_up_date:'', notes:'' });
    load();
  };

  const counts = STAGES.reduce((acc, s) => {
    acc[s] = s === 'all' ? prospects.length : prospects.filter(p => p.stage === s).length;
    return acc;
  }, {});
  const overdueCount = prospects.filter(isOverdue).length;

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
          <h1 style={{ fontSize:'24px', fontWeight:700 }}>Prospect Pipeline</h1>
          <div style={{ display:'flex', gap:'10px' }}>
            <button
              onClick={() => setShowFca(true)}
              style={{ background:'#0e1b2c', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 16px', fontWeight:600, fontSize:'13px', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}
            >
              🏛 Import from FCA
            </button>
            <button
              onClick={() => setShowAdd(true)}
              style={{ background:'#10b981', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 20px', fontWeight:600, fontSize:'14px', cursor:'pointer' }}
            >
              + Add Prospect
            </button>
          </div>
        </div>
        <p style={{ color:'#6b7280', marginBottom:'24px' }}>Track and manage potential broker firm customers</p>

        {error && <div style={alertRed}>{error}</div>}

        {/* Stage filter tabs */}
        <div style={{ display:'flex', gap:'8px', marginBottom:'20px', flexWrap:'wrap' }}>
          {STAGES.map(s => {
            const active = stageFilter === s;
            const [bg, col] = STAGE_COLOURS[s] || ['#e5e7eb','#374151'];
            return (
              <button
                key={s}
                onClick={() => setStageFilter(s)}
                style={{
                  border:'none', borderRadius:'999px', padding:'6px 14px', cursor:'pointer', fontSize:'12px', fontWeight: active ? 700 : 500,
                  background: active ? (s === 'all' ? '#0e1b2c' : col) : (s === 'all' ? '#e5e7eb' : bg),
                  color: active ? '#fff' : (s === 'all' ? '#374151' : col),
                  transition:'all .15s',
                }}
              >
                {s === 'all' ? 'All' : s.replace(/_/g,' ')}
              </button>
            );
          })}
          {overdueCount > 0 && (
            <span style={{ marginLeft:'8px', background:'#fee2e2', color:'#991b1b', borderRadius:'999px', padding:'6px 14px', fontSize:'12px', fontWeight:700 }}>
              ⚠ {overdueCount} overdue
            </span>
          )}
        </div>

        {/* Table */}
        <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)', overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1.5fr 1.2fr 1fr 1fr 1fr', gap:'0', padding:'10px 16px', background:'#f9fafb', borderBottom:'1px solid #e5e7eb' }}>
            {['Company','Contact','Stage','Tier','Follow-up','Est. MRR'].map(h => (
              <div key={h} style={{ fontSize:'11px', fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:'.04em' }}>{h}</div>
            ))}
          </div>
          {loading ? (
            <div style={{ padding:'40px', textAlign:'center', color:'#6b7280' }}>Loading…</div>
          ) : prospects.length === 0 ? (
            <div style={{ padding:'40px', textAlign:'center', color:'#9ca3af' }}>No prospects found</div>
          ) : (
            prospects.map(p => {
              const overdue = isOverdue(p);
              return (
                <div
                  key={p.id}
                  onClick={() => navigate(`/owner/prospects/${p.id}`)}
                  style={{ display:'grid', gridTemplateColumns:'2fr 1.5fr 1.2fr 1fr 1fr 1fr', gap:'0', padding:'13px 16px', borderBottom:'1px solid #f3f4f6', cursor:'pointer', background: overdue ? '#fffbeb' : '#fff', transition:'background .1s' }}
                  onMouseEnter={e => { if (!overdue) e.currentTarget.style.background='#f9fafb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = overdue ? '#fffbeb' : '#fff'; }}
                >
                  <div>
                    <div style={{ fontWeight:600, fontSize:'14px', display:'flex', alignItems:'center', gap:'6px' }}>
                      {overdue && <span title="Follow-up overdue" style={{ color:'#ef4444', fontSize:'13px' }}>●</span>}
                      {p.company_name}
                    </div>
                    <div style={{ fontSize:'12px', color:'#6b7280', marginTop:'2px' }}>{p.contact_email}</div>
                    {p.referrer_name && (
                      <div style={{ fontSize:'11px', color:'#6d28d9', marginTop:'3px', fontWeight:600 }}>
                        ↪ Referred by {p.referrer_name}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize:'13px', alignSelf:'center', color:'#374151' }}>{p.contact_name}</div>
                  <div style={{ alignSelf:'center' }}>{stageBadge(p.stage)}</div>
                  <div style={{ fontSize:'12px', alignSelf:'center', color:'#374151', textTransform:'capitalize' }}>{p.tier_interest ? p.tier_interest.replace(/_/g,' ') : '—'}</div>
                  <div style={{ fontSize:'12px', alignSelf:'center', color: overdue ? '#ef4444' : '#6b7280', fontWeight: overdue ? 700 : 400 }}>
                    {p.follow_up_date ? new Date(p.follow_up_date).toLocaleDateString('en-GB') : '—'}
                  </div>
                  <div style={{ fontSize:'13px', alignSelf:'center', fontWeight:600, color:'#065f46' }}>
                    {p.estimated_mrr ? `£${parseFloat(p.estimated_mrr).toFixed(2)}` : '—'}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* FCA Import Modal */}
        {showFca && (
          <FcaImportModal onClose={() => setShowFca(false)} onImported={() => { load(); }} />
        )}

        {/* Add Prospect Modal */}
        {showAdd && (
          <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) setShowAdd(false); }}>
            <div style={modalStyle}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
                <h2 style={{ fontSize:'20px', fontWeight:700 }}>Add Prospect</h2>
                <button onClick={() => setShowAdd(false)} style={{ background:'none', border:'none', fontSize:'20px', cursor:'pointer', color:'#9ca3af' }}>&times;</button>
              </div>
              {formError && <div style={alertRed}>{formError}</div>}
              <form onSubmit={handleAdd}>
                <div style={grid2}>
                  {[
                    ['company_name','Company name *','text',true],
                    ['contact_name','Contact name *','text',true],
                    ['contact_email','Email *','email',true],
                    ['contact_phone','Phone','tel',false],
                  ].map(([field,label,type,req]) => (
                    <div key={field} style={{ marginBottom:'14px' }}>
                      <label style={labelStyle}>{label}</label>
                      <input required={req} type={type} value={form[field]} onChange={e => setForm(f => ({...f,[field]:e.target.value}))} style={inputStyle} />
                    </div>
                  ))}
                </div>
                <div style={grid2}>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Firm size (advisers)</label>
                    <input type="number" min="1" value={form.firm_size} onChange={e => setForm(f => ({...f,firm_size:e.target.value}))} style={inputStyle} />
                  </div>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Source</label>
                    <select value={form.source} onChange={e => setForm(f => ({...f,source:e.target.value}))} style={inputStyle}>
                      <option value="">Select…</option>
                      {['website','linkedin','referral','introducer_referral','cold_outreach','conference','inbound','other'].map(s => (
                        <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Tier interest</label>
                    <select value={form.tier_interest} onChange={e => setForm(f => ({...f,tier_interest:e.target.value}))} style={inputStyle}>
                      <option value="">Select…</option>
                      <option value="solo">Solo</option>
                      <option value="small_firm">Small Firm</option>
                      <option value="firm">Firm</option>
                    </select>
                  </div>
                  <div style={{ marginBottom:'14px' }}>
                    <label style={labelStyle}>Follow-up date</label>
                    <input type="date" value={form.follow_up_date} onChange={e => setForm(f => ({...f,follow_up_date:e.target.value}))} style={inputStyle} />
                  </div>
                </div>
                <div style={{ marginBottom:'14px' }}>
                  <label style={labelStyle}>Notes</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({...f,notes:e.target.value}))} style={{...inputStyle,minHeight:'72px',resize:'vertical'}} />
                </div>
                <button type="submit" disabled={saving} style={{ background:'#10b981', color:'#fff', border:'none', borderRadius:'8px', padding:'10px 24px', fontWeight:600, fontSize:'14px', cursor:'pointer', width:'100%' }}>
                  {saving ? 'Saving…' : 'Create Prospect'}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

const alertRed = { background:'#fee2e2', color:'#991b1b', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginBottom:'16px' };
const overlayStyle = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' };
const modalStyle = { background:'#fff', borderRadius:'14px', padding:'28px', maxWidth:'580px', width:'100%', maxHeight:'90vh', overflowY:'auto' };
const grid2 = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0 16px' };
const labelStyle = { display:'block', fontSize:'12px', fontWeight:600, color:'#374151', marginBottom:'4px' };
const inputStyle = { width:'100%', padding:'8px 12px', border:'1px solid #d1d5db', borderRadius:'6px', fontSize:'13px', fontFamily:'inherit', color:'#111827', background:'#fff', boxSizing:'border-box' };
